import moment from 'moment';
import { addMinutes, format, isValid, parse, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import type { Locale } from 'date-fns';

export const formatDateShort = (date: string) => {
  const momentDate = moment(date);
  return `${momentDate.format('llll').split(',')[0]}, ${momentDate
    .format('MMM Do YY')
    .split(' ')
    .slice(0, 2)
    .join(' ')}`;
};

// date-fns locale codes that write the month before the day and take an ordinal day
// suffix ("Fri, Sep 11th", "金, 9月 11日"). Every other locale writes the day first as a
// cardinal number, so applying the English shape to them produces nonsense — e.g. es
// renders "Vie, sep 11º" instead of "Vie, 11 sep".
const MONTH_FIRST_LOCALE_CODES = [
  'en-US',
  'en-GB',
  'ja',
  'ko',
  'zh-CN',
  'zh-TW',
];

// Per-locale overrides keyed by date-fns locale.code, for formats that differ from the
// month-first/day-first defaults below. Add entries here as translators request them.
// All variants of a language (e.g. fr-FR, fr-CA) resolve to the same date-fns locale code
// via getDateLocale, so a single entry covers all regional variants.
//
// pt spells weekday and month out in full on purpose. Partner sites commonly run a
// page-level machine translation layer (Weglot, Google Translate) over the rendered embed,
// and abbreviated Portuguese collides head-on with English vocabulary: "sex" (sexta-feira),
// "set" (setembro), "out" (outubro), "ago" (agosto), "mar" (março). Those get re-translated
// as the English words — "Sex, set 11" came back as "Sexo, 11.º conjunto" on a live site.
// The full forms (sexta-feira, setembro, ...) have no English homographs, so they survive.
const LOCALE_FORMAT_OVERRIDES: Partial<
  Record<string, { long?: string; monthDay?: string; short?: string }>
> = {
  fr: { short: 'eee, d MMMM' }, // "Sam, 6 juin" — full month rather than the "juin"/"sept." mix
  pt: {
    long: "eeee, d 'de' MMMM", // "Sexta-feira, 11 de setembro"
    monthDay: "d 'de' MMMM", // "11 de setembro"
    short: "eeee, d 'de' MMMM", // no safe abbreviation exists — see note above
  },
};

const MONTH_FIRST_DATE_FORMAT = 'eee, MMM do'; // "Sat, Jun 6th"
const DAY_FIRST_DATE_FORMAT = 'eee, d MMM'; // "Ven, 11 set"

const deriveDateFormat = (
  locale: Locale,
  variant: 'long' | 'monthDay' | 'short',
  monthFirstFormat: string,
  dayFirstFormat: string,
) =>
  LOCALE_FORMAT_OVERRIDES[locale.code]?.[variant] ??
  (MONTH_FIRST_LOCALE_CODES.includes(locale.code)
    ? monthFirstFormat
    : dayFirstFormat);

// Capitalizes the first letter and removes the period that date-fns appends to some
// abbreviated day names (e.g. "sam." in French) when followed by the comma separator.
const normalizeEventDateOutput = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).replace('.,', ',');

/**
 * Format date for event listings where only the day matters (not the time).
 * Parses only the date part (YYYY-MM-DD) to avoid timezone conversion issues.
 * Example (en): "2025-11-22T03:00:00.000Z" -> "Sat, Nov 22nd"
 * Example (fr): "2025-11-22T03:00:00.000Z" -> "Sam, 22 novembre"
 * Example (pt): "2025-11-22T03:00:00.000Z" -> "Sábado, 22 de novembro"
 */
export const formatEventDateShort = (date: string, locale: Locale = enUS) => {
  const dateOnly = date.split('T')[0];
  const formatStr = deriveDateFormat(
    locale,
    'short',
    MONTH_FIRST_DATE_FORMAT,
    DAY_FIRST_DATE_FORMAT,
  );
  return normalizeEventDateOutput(
    format(parseISO(dateOnly), formatStr, { locale }),
  );
};

export const formatTimeShort = (time: string, locale: Locale = enUS) =>
  format(parse(time, 'HH:mm:ss', new Date()), 'p', { locale });

const MONTH_FIRST_DATE_LONG_FORMAT = 'eeee, MMMM do'; // "Saturday, June 6th"
const DAY_FIRST_DATE_LONG_FORMAT = 'eeee, d MMMM'; // "Samedi, 6 juin"

/**
 * Full weekday + date. Example (en): "Saturday, June 6th" / Example (fr): "Samedi, 6 juin"
 * Example (pt): "Sábado, 6 de junho"
 */
export const formatEventDateLong = (date: string, locale: Locale = enUS) => {
  const parsed = parseISO(date.split('T')[0]);
  if (!isValid(parsed)) return '';
  const formatStr = deriveDateFormat(
    locale,
    'long',
    MONTH_FIRST_DATE_LONG_FORMAT,
    DAY_FIRST_DATE_LONG_FORMAT,
  );
  return normalizeEventDateOutput(format(parsed, formatStr, { locale }));
};

const MONTH_FIRST_MONTH_DAY_FORMAT = 'MMMM do'; // "June 6th"
const DAY_FIRST_MONTH_DAY_FORMAT = 'd MMMM'; // "6 juin"

/**
 * Month + day only. Example (en): "June 6th" / Example (fr): "6 juin"
 * Example (pt): "6 de junho"
 */
export const formatEventMonthDay = (date: string, locale: Locale = enUS) => {
  const parsed = parseISO(date.split('T')[0]);
  if (!isValid(parsed)) return '';
  const formatStr = deriveDateFormat(
    locale,
    'monthDay',
    MONTH_FIRST_MONTH_DAY_FORMAT,
    DAY_FIRST_MONTH_DAY_FORMAT,
  );
  return normalizeEventDateOutput(format(parsed, formatStr, { locale }));
};

/**
 * Locale-aware replacement for the moment-based `formatDateForDisplay`.
 * Example (en): "2025-11-22T03:00:00.000Z" -> "Nov 22, 2025"
 * Example (fr): "2025-11-22T03:00:00.000Z" -> "22 nov. 2025"
 */
export const formatDateForDisplay = (
  date: string,
  locale: Locale = enUS,
): string => {
  if (!date) return '';
  const dateOnly = date.split('T')[0];
  const parsed = parseISO(dateOnly);
  if (!isValid(parsed)) return '';
  return format(parsed, 'PP', { locale });
};

export const extractTimesFromDateTime = (
  dateTime: string | undefined,
): { datePart: string; timePart: string } => {
  if (!dateTime) return { datePart: '', timePart: '' };
  const [datePart, timePart] = dateTime.split('T');
  return { datePart, timePart };
};

/**
 * Formats a start time + duration into separate locale-aware time strings.
 * Extracts the time directly from the HH:mm:ss string — no timezone conversion.
 * Example (en): startTime="14:30:00", duration=240 -> { startTime: "2:30 PM", endTime: "6:30 PM" }
 * Example (fr): startTime="14:30:00", duration=240 -> { startTime: "14:30", endTime: "18:30" }
 */
export const formatSessionTimeRange = (
  startTime: string,
  durationMinutes: number,
  locale: Locale = enUS,
): { startTime: string; endTime: string } => {
  const startParsed = parse(startTime, 'HH:mm:ss', new Date());
  return {
    startTime: format(startParsed, 'p', { locale }),
    endTime: format(addMinutes(startParsed, durationMinutes), 'p', { locale }),
  };
};

/**
 * Full weekday + day + month in day-before-month order for all locales.
 * Used where the date is already in local/venue time (no timezone conversion).
 * Example (en): "Friday, 29 May" / Example (fr): "Vendredi, 29 mai"
 */
export const formatResourceDateLong = (date: string, locale: Locale = enUS) => {
  const dateOnly = date.split('T')[0];
  return normalizeEventDateOutput(
    format(parseISO(dateOnly), 'eeee, d MMMM', { locale }),
  );
};

export const findFirstDateInThefuture = (
  dates: string[] = [],
  startTimes: string[] = [],
) => {
  const nowDateTime = moment().format().substring(0, 19);
  const lastStartTime = startTimes.sort().reverse()[0] || '23:59:59';

  return dates
    .filter((date) => {
      const dateTime = `${date}T${lastStartTime}`;
      return dateTime > nowDateTime;
    })
    .sort()[0];
};

/**
 * Format a time as "h:mm a"-style AM/PM. Accepts a `Date`, an ISO datetime
 * string, or a bare "HH:mm:ss" string. Returns '' for nil/unparseable input
 * so missing values (e.g. `sessions[0]?.startTime`) don't crash callers.
 */
export const formatTimeInAMPM = (
  time: string | Date | null | undefined,
  locale: Locale = enUS,
): string => {
  if (!time) return '';

  let date: Date | null = null;
  if (time instanceof Date) {
    date = time;
  } else if (typeof time === 'string') {
    date = parseISO(time);
    if (!isValid(date)) {
      // For OpenExperienceView and ExperienceBookingSidebar, the time is in the format of "HH:mm"
      // For RadioWrapper, the time is in the format of "HH:mm:ss"
      const timeFormat = time.length === 5 ? 'HH:mm' : 'HH:mm:ss';
      date = parse(time, timeFormat, new Date());
    }
  }

  if (!date || !isValid(date)) return '';

  return format(date, 'p', { locale });
};
